import { useEffect, useRef } from "react";

/**
 * useAutoFocus
 *
 * Automatically focuses inside a portal container when opened,
 * and optionally restores focus when closed.
 * Supports external ref forwarding.
 *
 * @param {boolean} active - Whether the portal is open
 * @param {object} options
 * @param {boolean} [options.restoreFocus=true] - Restore focus to previous element on close
 * @param {React.RefObject | ((node: HTMLElement | null) => void)} [options.externalRef] - Optional external ref
 * @returns {React.RefObject<HTMLElement>} - The merged ref for your portal container
 */
export function useAutoFocus(
  active,
  { restoreFocus = true, externalRef, tabTargetSelector } = {}
) {
  const internalRef = useRef(null);
  const lastActiveElement = useRef(null);

  // Merge internal + external refs
  useEffect(() => {
    if (!externalRef) return;
    if (typeof externalRef === "function") {
      externalRef(internalRef.current);
    } else if (typeof externalRef === "object" && externalRef !== null) {
      externalRef.current = internalRef.current;
    }
  }, [externalRef]);

  useEffect(() => {
    if (active) {
      lastActiveElement.current = document.activeElement;

      requestAnimationFrame(() => {
        const container = internalRef.current;
        if (!container) return;

        const firstTabbable = container.querySelector(
          tabTargetSelector ||
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        (firstTabbable || container).focus();
      });
    } else if (restoreFocus && lastActiveElement.current instanceof HTMLElement) {
      lastActiveElement.current.focus();
    }
  }, [active, restoreFocus, tabTargetSelector]);

  return internalRef;
}
